// src/boot.ts
const $$ = (sel: string, root: ParentNode = document) =>
  Array.from(root.querySelectorAll<HTMLElement>(sel));

const ACCOUNT_ENTRY_SELECTOR = "[data-account-entry]";

type InitFn = (root: HTMLElement) => void;
type ModLike =
  | { init?: InitFn; default?: any }
  | { default?: { init?: InitFn } };

type Loader = () => Promise<ModLike>;
type AccountSession = {
  activeAccount?: { username?: unknown } | null;
  identity?: { activeAccount?: { username?: unknown } | null } | null;
} | null;

/**
 * TS loaders (compile-time stable)
 * These import from src/modules/*
 */
const LOADERS: Record<string, Loader> = {
  A: () => import("./modules/A"),
  Bmain: () => import("./modules/Bmain"),
  Plant: () => import("./modules/Plant"),
  U: () => import("./modules/U"),
  welcome: () => import("./modules/Welcome"),
};

function resolveInit(modNs: any): InitFn | null {
  if (typeof modNs?.init === "function") return modNs.init;
  if (typeof modNs?.default === "function") return modNs.default;
  if (typeof modNs?.default?.init === "function") return modNs.default.init;
  return null;
}

function normalizeUsername(value: unknown) {
  return String(value || "").trim().replace(/^@+/, "").slice(0, 120);
}

function accountHrefFromSession(session: AccountSession) {
  const active = session?.activeAccount || session?.identity?.activeAccount || null;
  const username = normalizeUsername(active?.username);
  return username ? `/${encodeURIComponent(username)}` : "";
}

async function getActiveAccountHref() {
  try {
    const response = await fetch("/api/accounts/me", {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return "";
    const session = await response.json().catch(() => null);
    return accountHrefFromSession(session);
  } catch (_) {
    return "";
  }
}

function plainPrimaryClick(event: MouseEvent) {
  return event.button === 0 &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey;
}

function navigateTo(href: string) {
  if (typeof window.location.assign === "function") {
    window.location.assign(href);
    return;
  }
  window.location.href = href;
}

function setupAccountEntryLinks() {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(ACCOUNT_ENTRY_SELECTOR));
  if (!links.length || typeof fetch !== "function") return;

  let resolvedHref = "";
  let lookup: Promise<string> | null = null;
  const resolveHref = () => {
    if (!lookup) lookup = getActiveAccountHref();
    return lookup;
  };

  resolveHref().then((href) => {
    resolvedHref = href;
    if (!href) return;
    links.forEach((link) => { link.href = href; });
  });

  links.forEach((link) => {
    link.addEventListener("click", async (event) => {
      if (!plainPrimaryClick(event)) return;

      event.preventDefault();
      const href = resolvedHref || await resolveHref();
      navigateTo(href || link.getAttribute("href") || "/login/");
    });
  });
}

async function hydrateEl(el: HTMLElement) {
  const modName = el.dataset.module;
  if (!modName) return;

  if (el.dataset.hydrated === "1") return;
  el.dataset.hydrated = "1";

  const loader = LOADERS[modName];
  if (!loader) {
    console.warn(`[boot] No loader for data-module="${modName}"`);
    return;
  }

  try {
    const modNs = await loader();
    const init = resolveInit(modNs);
    if (!init) {
      console.warn(`[boot] Module "${modName}" has no init export`, modNs);
      return;
    }
    init(el);
  } catch (err) {
    console.error(`[boot] Failed to load module "${modName}"`, err);
  }
}

function hydrateAll(strategy: "visible" | "idle" = "visible") {
  const nodes = $$("[data-module]");

  // Immediate hydration for anything tagged
  nodes
    .filter((n) => n.dataset.hydrate === "immediate")
    .forEach((n) => hydrateEl(n));

  const remaining = nodes.filter((n) => n.dataset.hydrate !== "immediate");

  if (strategy === "idle" && "requestIdleCallback" in window) {
    (window as any).requestIdleCallback(
      () => remaining.forEach(hydrateEl),
      { timeout: 2000 }
    );
    return;
  }

  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          io.unobserve(e.target);
          hydrateEl(e.target as HTMLElement);
        }
      }
    },
    { rootMargin: "200px" }
  );

  remaining.forEach((n) => io.observe(n));
}

document.addEventListener("DOMContentLoaded", () => {
  setupAccountEntryLinks();
  hydrateAll("visible");
});
